import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Droplets } from 'lucide-react';
import Seo, { breadcrumbSchema } from '@/components/Seo';
import { SectionHeading, EmergencyCta } from '@/components/sections';
import { SERVICES, ICON_MAP, IMAGES } from '@/lib/site';
import { RESOURCES } from '@/lib/resources';

function Icon({ name, className }) {
  const C = ICON_MAP[name] || Droplets;
  return <C className={className} strokeWidth={1.9} />;
}

export default function ServicesPage() {
  return (
    <>
      <Seo
        title="Water Damage Restoration Services Tri-Cities BC"
        description="Water extraction, flood cleanup, burst pipe water damage, structural drying, mould concerns and sewage cleanup across Coquitlam, Port Coquitlam and Port Moody."
        schema={breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Services', path: '/services' }])}
      />

      <section className="relative overflow-hidden bg-navy">
        <img src={IMAGES.equipment} alt="Professional water damage restoration equipment" className="absolute inset-0 h-full w-full object-cover opacity-50" loading="eager" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/90 via-navy-deep/75 to-navy/25" />
        <div className="container-page relative py-16 lg:py-20">
          <span className="inline-block rounded-full bg-water/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-water ring-1 ring-water/20">Water Damage Services</span>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl">
            Water Damage Help for the <span className="text-water">Tri-Cities</span>
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-white/70">
            Choose the service that best matches the problem. If you are not sure, the request form can still be used and the situation can be assessed from there.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="container-page">
          <SectionHeading
            eyebrow="Choose a Service"
            title="Start with the problem you can see"
            sub="Each service page explains what the issue can involve, what usually happens next, and when professional help may be useful."
          />
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <article key={s.slug} className="group flex flex-col rounded-2xl border border-border bg-white p-7 transition hover:-translate-y-1 hover:border-water/40 hover:shadow-xl hover:shadow-navy/5">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-water-light text-water-strong">
                  <Icon name={s.icon} className="h-7 w-7" />
                </span>
                <h2 className="mt-5 font-display text-xl font-bold text-primary">{s.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.short}</p>
                <Link to={`/services/${s.slug}`} className="mt-auto pt-6 inline-flex items-center gap-1 text-sm font-semibold text-water-strong group-hover:gap-2 transition-all">
                  Learn about this service <ChevronRight className="h-4 w-4" />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-border bg-secondary py-16 sm:py-20"><div className="container-page"><SectionHeading eyebrow="Homeowner Guides" title="Learn What to Do Before and After Water Damage" sub="Short guides for common situations, with practical first steps and links to the services that may be involved."/><div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-4">{RESOURCES.map(a=><Link key={a.slug} to={`/resources/${a.slug}`} className="group rounded-2xl border border-border bg-white p-5 transition hover:-translate-y-1 hover:border-water/40 hover:shadow-lg"><p className="text-xs font-semibold uppercase tracking-wider text-water-strong">{a.readTime}</p><h2 className="mt-3 font-display text-lg font-bold leading-snug text-primary">{a.title}</h2><span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-water-strong">Read guide <ChevronRight className="h-4 w-4 transition group-hover:translate-x-1"/></span></Link>)}</div></div></section>

      <section className="py-16">
        <div className="container-page text-center">
          <h2 className="font-display text-3xl font-extrabold text-primary">Not sure which service fits?</h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Water damage often involves more than one step. Start with the visible problem and describe what happened in the request form.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="inline-flex items-center gap-1 rounded-full bg-navy-deep px-6 py-3 text-sm font-bold text-white transition hover:bg-navy">
              Request Water Damage Help <ChevronRight className="h-4 w-4" />
            </Link>
            <Link to="/areas" className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-6 py-3 text-sm font-bold text-primary transition hover:border-water/40 hover:text-water-strong">
              View Service Areas <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <EmergencyCta />
    </>
  );
}
